import { D } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import { useShops } from "../utils";
import ShopCard from "./ShopCard";
import sc from "./ShopCard.module.css";
import s from "./ShopGrid.module.css";
import { Skeleton } from "../../../themeV2";

const SpinnerIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" opacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);

const SKELETON_COUNT = 6;

function SkeletonShopCard() {
  return (
    <div
      className={sc.card}
      style={{ pointerEvents: "none" }}
      aria-hidden="true"
    >
      <div className={sc.imageWrap}>
        <Skeleton variant="rect" style={{ width: "100%", height: "100%" }} />
      </div>
      <div className={sc.info}>
        <Skeleton variant="text" style={{ width: "75%", marginBottom: 4 }} />
        <Skeleton variant="text" style={{ width: "55%" }} />
      </div>
    </div>
  );
}

const formatTotal = (total) => (total >= 1000 ? "999+" : String(total));

const resultsTextFor = ({ filter, pagination }) => {
  if (filter.name) {
    const total = formatTotal(pagination.totalResults);
    const resultText = pagination.totalResults === 1 ? "result" : "results";

    return `Showing ${total} ${resultText} for "${filter.name}"`;
  }

  const total = formatTotal(pagination.totalResults);
  const shopText = pagination.totalResults === 1 ? "shop" : "shops";

  return `${total} ${shopText}`;
};

export default function ShopGrid({ filter, setFilter }) {
  const { shops, pagination, isLoading, isValidating } = useShops(filter);
  const [cursorStack, setCursorStack] = React.useState([]);

  React.useEffect(() => {
    document.getElementById("root").scrollIntoView({ behavior: "smooth" });
  }, [filter.cursor]);

  const filterIdentity = React.useMemo(
    () =>
      JSON.stringify({
        order: filter.order,
        franchise: filter.franchise,
        type: filter.type,
        category: filter.category,
        name: filter.name,
      }),
    [filter.order, filter.franchise, filter.type, filter.category, filter.name],
  );

  React.useEffect(() => {
    setCursorStack([]);
  }, [filterIdentity]);

  const handlePrevious = React.useCallback(() => {
    const previousCursor = cursorStack.at(-1) ?? "";
    setCursorStack((stack) => stack.slice(0, -1));
    setFilter(D.merge({ cursor: previousCursor }));
  }, [cursorStack, setFilter]);

  const handleNext = React.useCallback(() => {
    if (!pagination.nextCursor) return;
    setCursorStack((stack) => [...stack, filter.cursor || ""]);
    setFilter(D.merge({ cursor: pagination.nextCursor }));
  }, [filter.cursor, pagination.nextCursor, setFilter]);

  const gridClass =
    isValidating && !isLoading ? `${s.grid} ${s.gridLoading}` : s.grid;

  return (
    <div>
      <div className={s.header}>
        <h2 className={s.title}>
          Shops
          {isValidating && !isLoading && <SpinnerIcon />}
        </h2>
        <span className={s.resultsText} aria-live="polite">
          {isLoading ? (
            <span aria-hidden="true">
              <Skeleton
                variant="text"
                as="span"
                style={{ width: 80, display: "inline-block" }}
              />
            </span>
          ) : (
            resultsTextFor({ filter, pagination })
          )}
        </span>
      </div>

      {isLoading ? (
        <div className={s.grid}>
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <SkeletonShopCard key={i} />
          ))}
        </div>
      ) : (
        <>
          <div className={gridClass}>
            {shops.map((shop) => (
              <ShopCard key={shop.influencerId} shop={shop} />
            ))}
          </div>

          {shops.length === 0 && <p className={s.empty}>No shops found</p>}

          {shops.length > 0 && (
            <nav className={s.pagination} aria-label="Shop pagination">
              <button
                type="button"
                className={s.paginationBtn}
                disabled={cursorStack.length === 0}
                onClick={handlePrevious}
              >
                Previous
              </button>
              <button
                type="button"
                className={s.paginationBtn}
                disabled={!pagination?.hasNext}
                onClick={handleNext}
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

ShopGrid.propTypes = {
  filter: PropTypes.shape({
    name: PropTypes.string,
    cursor: PropTypes.string,
  }).isRequired,
  setFilter: PropTypes.func.isRequired,
};
